<template>
    <b-container fluid class="bg-light-gray pt-2">
        <b-table
            :busy="isLoading"
            class="report-table"
            responsive="sm"
            borderless
            :fields="fields"
            :items="tableItems"
            show-empty
            :empty-text="$t('people.emptyTable')"
        >
            <template #table-busy>
                <div class="text-center my-2">
                    <b-spinner class="align-middle" />
                </div>
            </template>

            <template #head(_select)>
                <div class="actions-cell float-right pt-3 delete-selected-btn">
                    <b-button
                        variant="outline"
                        class="gray delete"
                        @click="deleteSelectedEvaluations"
                        v-if="!isSelectedEmpty"
                    >
                        <i class="fas fa-trash fa-2x"></i>
                    </b-button>
                </div>
            </template>
            <template #head(name)>
                <div class="header-style pl-3">
                    {{ $t('values.' + list) }}
                </div>
            </template>
            <template #head(teams)="data">
                <div class="header-style pl-3">
                    {{ data.label }}
                </div>
            </template>
            <template #head(groups)="data">
                <div class="header-style pl-3">
                    {{ data.label }}
                </div>
            </template>
            <template #head(total_evaluations)="data">
                <div class="header-style justify-content-center">
                    {{ data.label }}
                </div>
            </template>

            <template #cell(_select)="{ item }">
                <b-form-checkbox v-model="selected[item.id]" class="select-checkbox"> </b-form-checkbox>
            </template>
            <template #empty="row">
                <div class="empty-text">
                    {{ row.emptyText }}
                </div>
            </template>
            <template #cell(name)="row">
                <div class="cell-style user-name" @click="showDetails(row)">
                    <Avatar
                        class="d-inline-block"
                        :src="row.item.picture"
                        :username="row.item.name"
                        :size="40"
                        :customStyle="{
                            marginLeft: '5px',
                            marginRight: '15px',
                        }"
                    />
                    <span>{{ row.item.name }}</span>
                </div>
            </template>

            <template #cell(teams)="item">
                <div class="cell-style">
                    <span>{{ formatTeamsOrGroups(item.value) }}</span>
                </div>
            </template>
            <template #cell(groups)="item">
                <div class="cell-style">
                    <span v-if="item.value.length > 0">{{ formatTeamsOrGroups(item.value) }}</span>
                    <span v-else> - </span>
                </div>
            </template>
            <template #cell(total_evaluations)="{ item }">
                <div class="cell-style justify-content-center">
                    <span>{{ countTotalSentEvaluations(item) }} / {{ countTotalEvaluations(item) }}</span>
                </div>
            </template>
            <template #cell(show_details)="row">
                <div class="actions-cell">
                    <b-button variant="link" @click="showDetails(row)" class="gray">
                        <i v-if="!row.item._showDetails" class="fas fa-arrow-down"></i>
                        <i v-else class="fas fa-arrow-up"></i>
                    </b-button>
                    <b-button variant="outline" @click="deleteAllEvaluationsOfUser(row.item)" class="gray delete">
                        <i class="fas fa-trash"></i>
                    </b-button>
                </div>
            </template>
            <template #row-details="row">
                <b-table
                    class="details-table"
                    :items="getDetailsItems(row)"
                    :fields="detailsTableFields"
                    borderless
                    fixed
                >
                    <template #head(details_name)>
                        {{ $t('values.report.' + listDetailName + '_name') }}
                    </template>
                    <template #head(details_teams)>
                        {{ $t('values.report.team') }}
                    </template>
                    <template #head(details_groups)>
                        {{ $t('values.report.group') }}
                    </template>
                    <template #head(details_type)>
                        {{ $t('values.report.type') }}
                    </template>
                    <template #cell(details_name)="{ item }">
                        <span>{{ item[listDetailName].name }}</span>
                    </template>
                    <template #cell(details_teams)="{ item }">
                        <span>{{ formatTeamsOrGroups(item[listDetailName].teams) }}</span>
                    </template>
                    <template #cell(details_groups)="{ item }">
                        <span v-if="item[listDetailName].groups.length > 0">{{
                            formatTeamsOrGroups(item[listDetailName].groups)
                        }}</span>
                        <span v-else>-</span>
                    </template>
                    <template #cell(details_type)="{ item }">
                        <span>{{ $t('values.network.types.' + item.type) }}</span>
                    </template>
                    <template #cell(details_status)="{ item }">
                        <span>{{ $t('values.evaluationStatus.' + item.status) }}</span>
                    </template>
                    <template #cell(details_actions)="{ item }">
                        <div class="cell d-flex align-items-center">
                            <b-button
                                variant="outline"
                                class="gray delete btn-trash"
                                @click="deleteEvaluation(row, item)"
                                v-if="row.item[listType].length > 0"
                            >
                                <i class="fas fa-trash"></i>
                            </b-button>
                        </div>
                    </template>
                </b-table>
            </template>
        </b-table>
    </b-container>
</template>

<script>
import Avatar from 'vue-avatar'
import i18n from '@/i18n'
import { deleteAllEvaluationsOfUser, deleteEvaluation, deleteNetworkByUsers } from '@/views/values/api'
import { proceedWithConfirmation } from '@/lib/sweet-alert'
import { success, danger } from '@/lib/notification'

export default {
    name: 'EvaluationsList',
    props: {
        tableItems: {
            type: Array,
            required: true,
        },
        questionnaire: {
            type: String,
            required: true,
        },
        list: {
            type: String,
            required: true,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            fields: [
                {
                    key: '_select',
                    label: '',
                    thStyle: {
                        width: '5%',
                    },
                },
                {
                    key: 'name',
                    class: 'text-left',
                    thStyle: {
                        width: '20%',
                    },
                },
                {
                    key: 'teams',
                    label: i18n.t('values.report.team'),
                    class: 'text-left',
                    thStyle: {
                        width: '20%',
                    },
                },
                {
                    key: 'groups',
                    label: i18n.t('values.report.group'),
                    class: 'text-left',
                    thStyle: {
                        width: '20%',
                    },
                },
                {
                    key: 'total_evaluations',
                    label: i18n.t('values.report.total_evaluations_sent'),
                    thStyle: {
                        width: '10%',
                    },
                },
                {
                    key: 'show_details',
                    label: '',
                    thStyle: {
                        width: '5%',
                    },
                },
            ],
            detailsTableFields: [
                {
                    key: 'details_select',
                    label: '',
                    thStyle: {
                        width: '5%',
                    },
                },
                {
                    key: 'details_name',
                    label: '',
                    thStyle: {
                        width: '20%',
                    },
                },
                {
                    key: 'details_teams',
                    label: 'Equipe',
                    thStyle: {
                        width: '25%',
                    },
                },
                {
                    key: 'details_groups',
                    label: 'Cargo',
                    thStyle: {
                        width: '20%',
                    },
                },
                {
                    key: 'details_type',
                    label: 'Tipo',
                    thStyle: {
                        width: '10%',
                    },
                },
                {
                    key: 'details_status',
                    label: 'Status',
                    thStyle: {
                        width: '10%',
                    },
                },
                {
                    key: 'details_actions',
                    label: '',
                    thStyle: {
                        width: '5%',
                    },
                },
            ],
            selected: {},
        }
    },
    components: {
        Avatar,
    },
    methods: {
        formatTeamsOrGroups(value) {
            return value.map((item) => item.name).join(', ')
        },
        showDetails(row) {
            row.toggleDetails()
        },
        getDetailsItems(row) {
            return row.item[this.listType]
        },
        deleteAllEvaluationsOfUser({ id, name }) {
            let listType = ''

            if (this.list === 'valuers') {
                listType = 'evaluateds'
            }
            if (this.list === 'evaluateds') {
                listType = 'valuers'
            }

            proceedWithConfirmation({
                onConfirm: () => {
                    return deleteAllEvaluationsOfUser(this.questionnaire, this.list, {
                        users: [id],
                    })
                },
                message: i18n.t('values.report.modal.delete_all_evaluations.confirmation', {
                    name: name,
                    list: i18n.t('values.report.' + this.list),
                }),
                input: 'checkbox',
                inputPlaceholder: i18n.t('values.report.modal.delete_all_evaluations.delete_user_from_questionnaire', {
                    list: i18n.t('values.report.' + listType),
                }),
                inputValidator: (result) => {
                    if (result) {
                        deleteAllEvaluationsOfUser(this.questionnaire, listType, {
                            users: [id],
                        })
                    }
                },
                callback: () => {
                    success(i18n.t('values.report.modal.delete_all_evaluations.success'))
                    this.selected = {}
                    this.$emit('update', id)
                },
                onError: () => {
                    danger(i18n.t('values.report.modal.delete_all_evaluations.error'))
                    this.selected = {}
                    throw new Error('Erro ao excluir avaliações')
                },
            })
        },
        deleteEvaluation(row, item) {
            var hideCheckboxIfSelfEvaluation = item.type === 'self' ? null : 'checkbox'

            var message = ''
            var inputPlaceholderMessage = ''

            if (this.list === 'valuers') {
                message = i18n.t('values.report.modal.delete_evaluation.confirmation', {
                    valuer: row.item.name,
                    evaluated: item[this.listDetailName].name,
                })
                inputPlaceholderMessage = i18n.t('values.report.modal.delete_evaluation.network_delete_confirmation', {
                    valuer: row.item.name,
                    evaluated: item[this.listDetailName].name,
                })
            }

            if (this.list === 'evaluateds') {
                message = i18n.t('values.report.modal.delete_evaluation.confirmation', {
                    valuer: item[this.listDetailName].name,
                    evaluated: row.item.name,
                })
                inputPlaceholderMessage = i18n.t('values.report.modal.delete_evaluation.network_delete_confirmation', {
                    valuer: item[this.listDetailName].name,
                    evaluated: row.item.name,
                })
            }

            proceedWithConfirmation({
                onConfirm: () => {
                    return deleteEvaluation(item.id)
                },
                message: message,
                input: hideCheckboxIfSelfEvaluation,
                inputPlaceholder: inputPlaceholderMessage,
                inputValidator: (result) => {
                    if (result) {
                        if (this.list === 'valuers') {
                            deleteNetworkByUsers(row.item.id, item[this.listDetailName].id)
                        }
                        if (this.list === 'evaluateds') {
                            deleteNetworkByUsers(item[this.listDetailName].id, row.item.id)
                        }
                    }
                },
                callback: () => {
                    success(i18n.t('values.report.modal.delete_evaluation.success'))
                    this.removeItemFromDetailTable(row, item)
                    if (row.item[this.listType].length == 0) {
                        this.$emit('update', row.item.id)
                    }
                },
                onError: () => {
                    danger(i18n.t('values.report.modal.delete_evaluation.error'))
                    throw new Error('Error on delete evaluation')
                },
            })
        },
        deleteSelectedEvaluations() {
            proceedWithConfirmation({
                onConfirm: () => {
                    let users = []
                    Object.keys(this.selected).forEach((id) => {
                        if (this.selected[id]) {
                            users.push(id)
                        }
                    })
                    return deleteAllEvaluationsOfUser(this.questionnaire, this.list, {
                        users: users,
                    })
                },
                message: i18n.t('values.report.modal.delete_selected_evaluations.confirmation'),
                title: i18n.t('values.report.modal.delete_selected_evaluations.title'),
                callback: () => {
                    success(i18n.t('values.report.modal.delete_selected_evaluations.success'))
                    this.selected = {}
                    this.$emit('refresh')
                },
                onError: () => {
                    danger(i18n.t('values.report.modal.delete_selected_evaluations.error'))
                    throw new Error('Erro ao excluir avaliação')
                },
            })
        },
        removeItemFromDetailTable(row, item) {
            row.item[this.listType] = row.item[this.listType].filter((i) => i.id !== item.id)
        },
        countTotalEvaluations(item) {
            return item[this.listType].length
        },
        countTotalSentEvaluations(item) {
            return item[this.listType].reduce((acc, i) => {
                if (i.status === 'sent') {
                    return acc + 1
                }
                return acc
            }, 0)
        },
    },
    computed: {
        listType() {
            if (this.list === 'valuers') {
                return 'evaluations_as_valuer'
            }
            if (this.list === 'evaluateds') {
                return 'evaluations_as_evaluated'
            }
        },
        listDetailName() {
            if (this.list === 'valuers') {
                return 'evaluated'
            }
            if (this.list === 'evaluateds') {
                return 'valuer'
            }
        },
        isSelectedEmpty() {
            return Object.keys(this.selected).every((key) => !this.selected[key])
        },
    },
    watch: {
        list() {
            this.selected = {}
        },
    },
}
</script>

<style scoped>
::v-deep .report-table {
    max-width: 100%;
    margin: 10px auto;
}

::v-deep .report-table .table td {
    padding: 0.35rem 0.35rem;
}
::v-deep .report-table .table th {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
}
::v-deep .cell-style {
    display: flex;
    align-items: center;
    padding: 10px 15px;
    border: 1px solid #c4c4c4;
    border-radius: 5px;
    min-height: 60px;
    background-color: #fff;
    font-size: 16px;
}
::v-deep .header-style {
    display: flex;
    align-items: center;
    justify-content: left;
    min-height: 55px;
    border-radius: 5px;
    background-color: #009ada;
    color: #fff;
    text-align: center;
    margin-bottom: -10px;
    font-size: 16px;
}
::v-deep .actions-cell {
    display: flex;
    align-items: center;
    justify-content: left;
    min-height: 55px;
}
::v-deep .bg-light-gray {
    background-color: #f9f9f9;
}
::v-deep .details-table {
    max-width: 97%;
    margin: 0 auto;
    font-size: 15px;
    margin-bottom: 10px;
}
::v-deep .gray {
    color: #7f7f7f;
}
::v-deep .delete:hover {
    color: #f86c6b;
}
::v-deep .select-checkbox {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-right: 15px;
    margin-top: 5px;
    transform: scale(1.2);
}
::v-deep .user-name:hover {
    cursor: pointer;
}
::v-deep .empty-text {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1rem;
    margin-top: 20px;
}
::v-deep .delete-selected-btn {
    width: 51px;
    height: 40px;
}
::v-deep .btn-trash {
    padding: 0;
}
@media screen and (max-width: 1367px) {
    ::v-deep .details-table {
        max-width: 95%;
    }
}

@media screen and (min-width: 1920px) {
    ::v-deep .report-table {
        max-width: 85%;
        margin: 10px auto;
    }
}
</style>
